import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import useAppStore from '../../store';
import ChannelCard from '../../components/channels/ChannelCard';
import RoutingRulesTable from '../../components/channels/RoutingRulesTable';
import AddRoutingRuleModal from '../../components/channels/AddRoutingRuleModal';
import TestMessageConsole from '../../components/channels/TestMessageConsole';
import MessageLogTable from '../../components/channels/MessageLogTable';
import DataModeBadge from '../../components/feedback/DataModeBadge';
import { getChannels } from '../../lib/api/channels';
import { RATE_LIMITS } from '../../mocks/channels';

const USAGE_THRESHOLDS = [
  { limit: 0.8, className: 'ratelimit--critical' },
  { limit: 0.55, className: 'ratelimit--warn' }
];

function rateLimitClass(usage) {
  return USAGE_THRESHOLDS.find((threshold) => usage >= threshold.limit)?.className ?? '';
}

/**
 * Channels screen (cutover §3). Reads are fetch-first: GET /api/channels on
 * mount over the 1789900004 collections; success flips channelsMode 'live'
 * (empty lists are a valid live state); failure keeps the sample seed behind
 * the honest badge. Test-send stays honest end-to-end: the console calls the
 * real 501 stub and surfaces "not yet live" — it never simulates a delivery.
 * Panels whose source does not exist (message log, rate limits) keep sample
 * data under explicitly labeled sections.
 */
function ChannelsPage() {
  const channels = useAppStore((state) => state.channels);
  const channelsMode = useAppStore((state) => state.channelsMode);
  const setChannelsData = useAppStore((state) => state.setChannelsData);
  const setChannelsMode = useAppStore((state) => state.setChannelsMode);
  const routingRules = useAppStore((state) => state.routingRules);
  const toggleChannel = useAppStore((state) => state.toggleChannel);
  const toggleAgentOnChannel = useAppStore((state) => state.toggleAgentOnChannel);
  const agents = useAppStore((state) => state.agents);
  const [composerOpen, setComposerOpen] = useState(false);
  const [testChannelId, setTestChannelId] = useState(null);

  // Fetch-first read (cutover §3). testChannelId is re-resolved against the
  // loaded list so the console never holds a stale selection.
  useEffect(() => {
    let cancelled = false;
    getChannels()
      .then((data) => {
        if (cancelled) return;
        setChannelsData(data || {}, 'live');
      })
      .catch(() => {
        // Backend unreachable — sample seed stays, honestly labeled.
        if (cancelled) return;
        setChannelsMode('sample');
      });
    return () => {
      cancelled = true;
    };
  }, [setChannelsData, setChannelsMode]);

  const testChannel = channels.find((channel) => channel.id === testChannelId) || null;

  const handleTest = (channel) => {
    setTestChannelId(channel.id);
    document.getElementById('channel-test-console')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <main className="page-content">
      <section className="page-heading">
        <div>
          <p className="eyebrow">NOTIFICATION HUB</p>
          <h1>
            Channels{' '}
            <DataModeBadge
              mode={channelsMode}
              label={channelsMode === 'live' ? null : 'Sample channels — backend unreachable'}
            />
          </h1>
          <span>Route agent events to Telegram, WhatsApp, email, and SMS.</span>
        </div>
        <button className="primary-button" onClick={() => setComposerOpen(true)} type="button">
          <FiIcons.FiPlus /> New rule
        </button>
      </section>

      <section className="channel-grid">
        {channels.map((channel) => (
          <ChannelCard
            channel={channel}
            key={channel.id}
            onTest={handleTest}
            onToggle={toggleChannel}
          />
        ))}
      </section>

      <section className="panel">
        <div className="section-heading">
          <div>
            <h2>
              Routing rules{' '}
              <DataModeBadge mode={channelsMode} label="Sample rules" />
            </h2>
            <span>When an agent fires an event, deliver it through a channel.</span>
          </div>
          <button className="ghost-button" onClick={() => setComposerOpen(true)} type="button">
            <FiIcons.FiPlus /> Compose
          </button>
        </div>
        <RoutingRulesTable rules={routingRules} />
      </section>

      <section className="panel" id="channel-test-console">
        <div className="section-heading">
          <div>
            <h2>Test message</h2>
            <span>
              Delivery probes are not live yet — the platform cannot send
              external messages (dry-run round). Requests hit the real
              endpoint and are honestly refused.
            </span>
          </div>
          {testChannel && <span className="test-console__armed">Armed: {testChannel.type}</span>}
        </div>
        <TestMessageConsole
          channel={testChannel}
          channels={channels}
          onChannelChange={setTestChannelId}
        />
      </section>

      <section className="panel">
        <div className="section-heading">
          <div>
            <h2>
              Message log <DataModeBadge mode="sample" label="Sample log — channel transport pending" />
            </h2>
            <span>Recent traffic, newest first.</span>
          </div>
        </div>
        <MessageLogTable />
      </section>

      <section className="panel">
        <div className="section-heading">
          <div>
            <h2>
              Rate limits <DataModeBadge mode="sample" label="Sample quotas — provider telemetry pending" />
            </h2>
            <span>Provider quotas observed by Agent 4 (Model Router output).</span>
          </div>
        </div>
        <ul className="ratelimit-list">
          {RATE_LIMITS.map((entry) => {
            const channel = channels.find((candidate) => candidate.type === entry.channelType);
            return (
              <li className={`ratelimit ${rateLimitClass(entry.usage)}`} key={entry.channelType}>
                <span className="ratelimit__channel">{channel?.name ?? entry.channelType}</span>
                <span className="ratelimit__quota">{entry.limit} {entry.window}</span>
                <span className="ratelimit__meter">
                  <i style={{ width: `${Math.round(entry.usage * 100)}%` }} />
                </span>
                <span className="ratelimit__pct">{Math.round(entry.usage * 100)}%</span>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="panel">
        <div className="section-heading">
          <div>
            <h2>
              Per-agent delivery{' '}
              <DataModeBadge
                mode={channelsMode}
                label={channelsMode === 'live' ? null : 'Sample roster'}
              />
            </h2>
            <span>Choose which agents may speak on each channel.</span>
          </div>
        </div>
        <table className="agent-channel-matrix">
          <thead>
            <tr>
              <th scope="col">Agent</th>
              {channels.map((channel) => (
                <th key={channel.id} scope="col">{channel.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {agents.map((agent) => (
              <tr key={agent.id}>
                <td>{agent.name}</td>
                {channels.map((channel) => {
                  const checked = channel.enabledAgentIds?.includes(agent.id) ?? false;
                  return (
                    <td key={channel.id}>
                      <input
                        aria-label={`${agent.name} on ${channel.name}`}
                        checked={checked}
                        onChange={() => toggleAgentOnChannel(channel.id, agent.id)}
                        type="checkbox"
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <AddRoutingRuleModal onClose={() => setComposerOpen(false)} open={composerOpen} />
    </main>
  );
}

export default ChannelsPage;
